import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowUpRight } from 'lucide-react';
import { positions, type Position } from '../data/experience';
import Reveal, { useReveal } from './ui/Reveal';

/**
 * One entry in the register. Its own component because each row observes itself
 * — an <li> cannot be wrapped in a <Reveal> div without breaking the <ol> — and
 * a hook cannot be called from inside a .map callback.
 */
const PositionRow: React.FC<{ exp: Position }> = ({ exp }) => {
  const { t } = useTranslation();
  const reveal = useReveal<HTMLLIElement>();

  return (
    <li {...reveal} className="row-ruled sm:grid-cols-[minmax(0,13rem)_1fr] sm:gap-x-10">
      <div className="ui text-[12px] font-semibold uppercase tracking-[0.16em] text-graphite">
        {t(`experience.positions.${exp.id}.period`)}
        <span className="mt-1.5 block font-medium normal-case tracking-[0.04em]">
          {exp.location}
        </span>
      </div>

      <div>
        <h3 className="ui text-[19px] font-bold leading-tight text-ink">
          {t(`experience.positions.${exp.id}.title`)}
        </h3>
        <p className="ui mt-1 text-[15px] text-lacquer-bright">
          {t(`experience.positions.${exp.id}.company`)}
        </p>

        <ul className="mt-4 space-y-2.5">
          {exp.achievements.map((a) => (
            <li key={a} className="flex gap-3 text-[15px] leading-relaxed text-graphite">
              <span aria-hidden="true" className="mt-2.5 h-px w-3 shrink-0 bg-hairline" />
              <span>{a}</span>
            </li>
          ))}
        </ul>

        <ul className="mt-4 flex flex-wrap gap-x-3 gap-y-1">
          {exp.technologies.map((tech) => (
            <li key={tech} className="tag">
              {tech}
            </li>
          ))}
        </ul>
      </div>
    </li>
  );
};

/**
 * A CV register: period in the left column, role in the right, separated by
 * hairlines. Deliberately not a vertical timeline with alternating cards — the
 * content is a plain reverse-chronological list and the structure should say so.
 */
const Experience: React.FC = () => {
  const { t } = useTranslation();

  return (
    <section id="experience" className="section">
      <div className="container-page">
        <Reveal>
          <p className="eyebrow">{t('experience.eyebrow')}</p>
          <h2 className="section-title mt-4 max-w-2xl">{t('experience.title')}</h2>
          <p className="mt-5 max-w-prose text-graphite">{t('experience.subtitle')}</p>
        </Reveal>

        <ol className="mt-10">
          {positions.map((exp) => (
            <PositionRow key={exp.id} exp={exp} />
          ))}

          <li className="row-ruled border-b border-hairline sm:grid-cols-[minmax(0,13rem)_1fr] sm:gap-x-10">
            <div className="ui text-[12px] font-semibold uppercase tracking-[0.16em] text-graphite">
              {t('experience.education.period')}
              <span className="mt-1.5 block font-medium normal-case tracking-[0.04em]">
                Damascus, Syria
              </span>
            </div>
            <div>
              <h3 className="ui text-[19px] font-bold leading-tight text-ink">
                {t('experience.education.degree')}
              </h3>
              <p className="ui mt-1 text-[15px] text-lacquer-bright">
                {t('experience.education.school')}
              </p>
              <p className="mt-4 text-[15px] leading-relaxed text-graphite">
                {t('experience.education.desc')}
              </p>
            </div>
          </li>
        </ol>

        <div className="mt-10">
          <a
            href="/Anas_Khaled_Jammal.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary"
          >
            {t('experience.downloadCv')}
            <ArrowUpRight size={17} aria-hidden="true" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default Experience;
